export type CrmContactOperatorBriefTone = "neutral" | "info" | "success" | "warning" | "danger";
export type CrmContactOperatorBriefPriority = "critical" | "high" | "medium" | "low";

export interface CrmContactOperatorBriefPoint {
  key: string;
  label: string;
  summary: string;
  detail: string | null;
  evidence: string | null;
  category: string | null;
  priority: CrmContactOperatorBriefPriority;
  tone: CrmContactOperatorBriefTone;
}

export interface CrmContactOperatorBrief {
  key: string;
  label: string;
  summary: string | null;
  scoreLabel: string | null;
  scoreTone: CrmContactOperatorBriefTone;
  points: CrmContactOperatorBriefPoint[];
  objections: CrmContactOperatorBriefPoint[];
  nextSteps: CrmContactOperatorBriefPoint[];
}

export interface BuildCrmContactOperatorBriefOptions {
  briefKeys?: readonly string[];
  maxPoints?: number;
  maxObjections?: number;
  maxNextSteps?: number;
}

export const CRM_CONTACT_OPERATOR_BRIEF_FIELD_KEYS = [
  "crmContactOperatorBrief",
  "contactOperatorBrief",
  "operatorBrief",
  "preCallBrief",
  "talkingPoints",
] as const;

const PRIVATE_KEY_PATTERN = /(?:password|secret|token|authorization|credential|private[_-]?key|api[_-]?key)/i;
const MAX_TEXT_LENGTH = 220;

const PRIORITY_RANK: Record<CrmContactOperatorBriefPriority, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

export function buildCrmContactOperatorBrief(
  customFields: unknown,
  options: BuildCrmContactOperatorBriefOptions = {},
): CrmContactOperatorBrief | null {
  const fields = asRecord(customFields);
  if (!fields) return null;

  const briefKeys = options.briefKeys?.length
    ? [...options.briefKeys]
    : [...CRM_CONTACT_OPERATOR_BRIEF_FIELD_KEYS];
  const maxPoints = normalizePositiveInteger(options.maxPoints, 8);
  const maxObjections = normalizePositiveInteger(options.maxObjections, 6);
  const maxNextSteps = normalizePositiveInteger(options.maxNextSteps, 5);

  for (const briefKey of briefKeys) {
    if (isPrivateKey(briefKey)) continue;
    const brief = parseOperatorBrief(
      briefKey,
      fields[briefKey],
      maxPoints,
      maxObjections,
      maxNextSteps,
    );
    if (brief) return brief;
  }

  return null;
}

function parseOperatorBrief(
  fallbackKey: string,
  value: unknown,
  maxPoints: number,
  maxObjections: number,
  maxNextSteps: number,
): CrmContactOperatorBrief | null {
  if (Array.isArray(value)) {
    const points = sortBriefPoints(
      value
        .map((entry, index) => parseBriefPoint(`point-${index + 1}`, entry, "talking_point"))
        .filter((point): point is CrmContactOperatorBriefPoint => Boolean(point)),
    ).slice(0, maxPoints);

    return points.length > 0
      ? {
        key: fallbackKey,
        label: "Operator brief",
        summary: null,
        scoreLabel: null,
        scoreTone: "neutral",
        points,
        objections: [],
        nextSteps: [],
      }
      : null;
  }

  const record = asRecord(value);
  if (!record) return null;

  const key = readString(record.key) ?? fallbackKey;
  if (isPrivateKey(key)) return null;

  const points = parseBriefPointList(record.points ?? record.items ?? record.talkingPoints ?? record.signals, "talking_point")
    .slice(0, maxPoints);
  const objections = parseBriefPointList(record.objections ?? record.risks ?? record.handlingNotes, "objection")
    .slice(0, maxObjections);
  const nextSteps = parseBriefPointList(record.nextSteps ?? record.recommendedActions ?? record.actions, "next_step")
    .slice(0, maxNextSteps);

  if (points.length === 0 && objections.length === 0 && nextSteps.length === 0) return null;

  return {
    key,
    label: truncate(readString(record.label) ?? readString(record.title) ?? formatLabel(key)) ?? formatLabel(key),
    summary: truncate(readString(record.summary) ?? readString(record.description)),
    scoreLabel: truncate(readString(record.scoreLabel) ?? readString(record.score) ?? readString(record.priorityLabel)),
    scoreTone: normalizeTone(readString(record.scoreTone) ?? readString(record.tone) ?? readString(record.status)),
    points,
    objections,
    nextSteps,
  };
}

function parseBriefPointList(value: unknown, fallbackCategory: string): CrmContactOperatorBriefPoint[] {
  if (Array.isArray(value)) {
    return sortBriefPoints(
      value
        .map((entry, index) => parseBriefPoint(`point-${index + 1}`, entry, fallbackCategory))
        .filter((point): point is CrmContactOperatorBriefPoint => Boolean(point)),
    );
  }

  const record = asRecord(value);
  if (!record) return [];

  return sortBriefPoints(
    Object.entries(record)
      .map(([key, entry]) => parseBriefPoint(key, entry, fallbackCategory))
      .filter((point): point is CrmContactOperatorBriefPoint => Boolean(point)),
  );
}

function parseBriefPoint(
  fallbackKey: string,
  value: unknown,
  fallbackCategory: string,
): CrmContactOperatorBriefPoint | null {
  if (isPrivateKey(fallbackKey)) return null;

  const record = asRecord(value);
  const key = record ? readString(record.key) ?? fallbackKey : fallbackKey;
  if (isPrivateKey(key)) return null;

  const label = record
    ? truncate(readString(record.label) ?? readString(record.title) ?? formatLabel(key)) ?? formatLabel(key)
    : formatLabel(key);
  const summary = record
    ? truncate(
      readString(record.summary)
      ?? readString(record.text)
      ?? readString(record.value)
      ?? readString(record.description)
      ?? readString(record.guidance),
    )
    : formatBriefValue(value);
  if (!summary) return null;

  return {
    key,
    label,
    summary,
    detail: record ? truncate(readString(record.detail) ?? readString(record.helpText)) : null,
    evidence: record ? truncate(readString(record.evidence) ?? readString(record.reason)) : null,
    category: record ? truncate(readString(record.category) ?? readString(record.type)) ?? fallbackCategory : fallbackCategory,
    priority: normalizePriority(record ? readString(record.priority) ?? readString(record.urgency) : null),
    tone: normalizeTone(record ? readString(record.tone) ?? readString(record.status) : null),
  };
}

function sortBriefPoints(points: CrmContactOperatorBriefPoint[]): CrmContactOperatorBriefPoint[] {
  return [...points].sort((left, right) => PRIORITY_RANK[left.priority] - PRIORITY_RANK[right.priority]);
}

function formatBriefValue(value: unknown): string | null {
  if (typeof value === "string") return truncate(value.trim());
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : null;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return null;
}

function normalizePriority(value: string | null): CrmContactOperatorBriefPriority {
  if (value === "critical" || value === "high" || value === "medium" || value === "low") return value;
  if (value === "urgent" || value === "blocker") return "critical";
  if (value === "normal" || value === "default") return "medium";
  return "medium";
}

function normalizeTone(value: string | null): CrmContactOperatorBriefTone {
  if (value === "success" || value === "warning" || value === "danger" || value === "info") return value;
  if (value === "critical" || value === "error" || value === "failed" || value === "blocked") return "danger";
  if (value === "ready" || value === "passed" || value === "complete" || value === "recommended") return "success";
  if (value === "review" || value === "hold" || value === "partial" || value === "urgent") return "warning";
  return "neutral";
}

function normalizePositiveInteger(value: number | undefined, fallback: number): number {
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : fallback;
}

function truncate(value: string | null): string | null {
  if (!value) return null;
  return value.length > MAX_TEXT_LENGTH ? `${value.slice(0, MAX_TEXT_LENGTH - 3)}...` : value;
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function isPrivateKey(key: string): boolean {
  return PRIVATE_KEY_PATTERN.test(key);
}

function formatLabel(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
